import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileCheck, Clock, CheckCircle } from 'lucide-react';
import { regulatoryServices as mockServices, filterRegulatoryServices } from '../data/mock';
import { contentAPI } from '../services/api';

const RegulatoryConsultancy = () => {
  const [regulatoryServices, setRegulatoryServices] = useState(mockServices);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('regulatory_services').then(res => {
      if (Array.isArray(res.data) && res.data.length) setRegulatoryServices(filterRegulatoryServices(res.data));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Regulatory Consultancy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Regulatory Consultancy</h1>
          <p className="text-xl text-gray-200 max-w-2xl">
            Expert regulatory approvals and compliance support for developers and EPC companies
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Navigate Regulations with Confidence</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our regulatory experts handle all statutory clearances, licenses, and compliance requirements for your renewable energy projects. From transmission line crossings to aviation clearances, we ensure smooth approvals and timely project execution.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regulatoryServices.map((service) => (
              <Link
                key={service.id}
                to={`/regulatory/${service.slug}`}
                className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FileCheck className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-200">
                      {service.title}
                    </h3>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {service.description}
                </p>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{service.timeline}</span>
                  </div>
                  <div className="flex items-center text-blue-600 font-medium group-hover:gap-2 transition-all duration-200">
                    View Details
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose SOLARIAN?</h2>
            <p className="text-lg text-gray-600">Your trusted partner for regulatory success</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Expert Team', desc: 'Experienced professionals with deep regulatory knowledge' },
              { title: 'Proven Track Record', desc: '250+ MW of successful project approvals' },
              { title: 'Timely Delivery', desc: 'Streamlined processes for faster clearances' },
              { title: 'End-to-End Support', desc: 'Complete assistance from application to approval' }
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-8 shadow-lg text-center transform hover:-translate-y-2 transition-all duration-300">
                <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Regulatory Support?
          </h2>
          <p className="text-xl text-blue-50 mb-8">
            Let our experts handle your approvals and clearances
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Request Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RegulatoryConsultancy;
